import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import axios from "axios";
import { createAuthReturnState, requireAdditionalInfoOnNextLogin } from "@/contexts/auth/redirect";
import { EnvVariables, isDevelopment } from "@/env-variables";
import { getClinicId } from "@/hooks/useClinicId";
import { analytics } from "@/lib/analytics";
import { getApiRequestMeta, getCurrentRoutePath } from "@/lib/analytics/api-utils";
import {
  normalizeErrorName,
  sanitizeErrorMessage,
  sanitizeErrorStack,
} from "@/lib/analytics/error-utils";
import { generateChallenge, generateCodeVerifier } from "@/lib/auth/pkce";
import { createFeRequestId, FE_REQUEST_ID_HEADER, FE_REQUEST_ID_PROPERTY } from "@/lib/request-id";
import { addSentryBreadcrumb, captureAxiosError, captureGlobalError } from "@/lib/sentry";
import * as storage from "@/lib/storage";
import { isTokenExpired, refreshToken } from "@/services/auth";
import sessionSocketService from "@/services/session-socket.service";
import * as LocalStorage from "@/storages/LocalStorage";
// import { StorageKeys } from "@/storages/storage-keys";
import { useAdditionalInfo } from "@/stores/domains/additionalInfo";
import { getRoom } from "@/stores/domains/additionalInfo/useAdditionalInfo";
import { resetAllStores } from "@/stores/utils/resetAllStores";
import { rewriteAbsolutePath, rewriteRelativePathVersion } from "@/utils/api-path-rewriter";
import { clearPersistedStores } from "@/utils/clearPersistedStores";
import { applyActiveRoleHeader, shouldSendActiveRole } from "./active-role-header";
import { extractApiErrorMessage, isConflictError } from "./core/error-message";

interface RequestInterceptor {
  onFulfilled: (config: InternalAxiosRequestConfig) => Promise<InternalAxiosRequestConfig>;
  onRejected: (error: AxiosError) => Promise<AxiosError>;
}

interface ResponseInterceptor {
  onFulfilled: (response: AxiosResponse) => Promise<AxiosResponse>;
  onRejected: (error: unknown) => Promise<unknown>;
}

// REMOVED: unwrapNestedData function to prevent any data modification

// Flag to prevent multiple token refresh requests
let isRefreshing = false;
// Store pending requests
let pendingRequests: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

type AnalyticsMeta = {
  startTime: number;
  routePath?: string;
  feRequestId?: string;
};

const nowMs = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

const getAnalyticsMeta = (config?: InternalAxiosRequestConfig): AnalyticsMeta | undefined => {
  return (config as InternalAxiosRequestConfig & { __analytics?: AnalyticsMeta })?.__analytics;
};

const trackApiPerformance = (
  config: InternalAxiosRequestConfig | undefined,
  status?: number,
  success?: boolean,
) => {
  if (!config) return;
  const meta = getAnalyticsMeta(config);
  if (!meta?.startTime) return;

  const duration = nowMs() - meta.startTime;
  if (!Number.isFinite(duration) || duration <= 0) return;

  const requestMeta = getApiRequestMeta(config);

  analytics.trackPerformance({
    metricName: "api.duration",
    duration,
    metadata: {
      api_path: requestMeta.path,
      api_host: requestMeta.host,
      api_method: requestMeta.method,
      api_status: status,
      api_success: success,
      route_path: meta.routePath || getCurrentRoutePath(),
      [FE_REQUEST_ID_PROPERTY]: meta.feRequestId,
    },
  });
};

const trackApiError = (error: AxiosError) => {
  const status = error.response?.status;
  const config = error.config;
  const requestMeta = config ? getApiRequestMeta(config) : undefined;
  const analyticsMeta = getAnalyticsMeta(config);
  const routePath = analyticsMeta?.routePath || getCurrentRoutePath();

  captureAxiosError(error, {
    api_path: requestMeta?.path,
    api_host: requestMeta?.host,
    api_method: requestMeta?.method,
    api_status: status,
    route_path: routePath,
    [FE_REQUEST_ID_PROPERTY]: analyticsMeta?.feRequestId,
  });

  analytics.trackError("API Error", sanitizeErrorMessage(error.message), {
    error_name: normalizeErrorName(error),
    error_stack: sanitizeErrorStack(error.stack),
    api_path: requestMeta?.path,
    api_host: requestMeta?.host,
    api_method: requestMeta?.method,
    api_status: status,
    route_path: routePath,
    [FE_REQUEST_ID_PROPERTY]: analyticsMeta?.feRequestId,
  });
};

const normalizeHeaderValue = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.join(",");
  if (value && typeof value === "object") return String(value);
  return "";
};

// Session initialization queue
let isWaitingForSession = false;
let sessionWaitPromise: Promise<void> | null = null;
const MAX_SESSION_WAIT_TIME = 10000; // 10 seconds timeout

/**
 * Wait for session to be initialized
 * This prevents requests from being sent before session ID is available
 */
const waitForSessionInitialization = async (): Promise<void> => {
  const existingSessionId = LocalStorage.getSessionId();
  const isInitializing = LocalStorage.getItem("session_initializing");
  const hasToken = !!LocalStorage.getAccessToken();

  // If session already exists, no need to wait
  if (existingSessionId) {
    return Promise.resolve();
  }

  // If no token, user is not logged in, no need to wait
  if (!hasToken) {
    return Promise.resolve();
  }

  // If initializing flag is set, we must wait
  // If no flag but no sessionId and has token, also wait (session registration may be starting)
  const shouldWait = isInitializing || (!existingSessionId && hasToken);

  if (!shouldWait) {
    return Promise.resolve();
  }

  // If already waiting, reuse the existing promise
  if (isWaitingForSession && sessionWaitPromise) {
    return sessionWaitPromise;
  }

  // Create a new wait promise
  isWaitingForSession = true;
  sessionWaitPromise = new Promise((resolve) => {
    const startTime = Date.now();
    const checkInterval = 50; // Check every 50ms

    const checkSession = () => {
      const sessionId = LocalStorage.getSessionId();
      const stillInitializing = LocalStorage.getItem("session_initializing");

      // Session is ready - sessionId exists
      if (sessionId) {
        isWaitingForSession = false;
        sessionWaitPromise = null;
        resolve();
        return;
      }

      // If flag is cleared but no sessionId, check if we should keep waiting
      // Give a short grace period for the flag to be set
      if (!stillInitializing && !sessionId) {
        const elapsed = Date.now() - startTime;
        // Wait at least 500ms for the initialization to start
        if (elapsed > 500) {
          isWaitingForSession = false;
          sessionWaitPromise = null;
          resolve(); // Let the request proceed
          return;
        }
      }

      // Timeout reached
      if (Date.now() - startTime > MAX_SESSION_WAIT_TIME) {
        isWaitingForSession = false;
        sessionWaitPromise = null;
        resolve(); // Don't reject, let the request proceed
        return;
      }

      // Keep waiting
      setTimeout(checkSession, checkInterval);
    };

    checkSession();
  });

  return sessionWaitPromise;
};

// Helper function to wait for store rehydration
const waitForStoreRehydration = async (maxWaitMs = 1000): Promise<boolean> => {
  const startTime = Date.now();
  const checkInterval = 50; // Check every 50ms

  // Record initial state to detect if store was reset during wait
  const initialState = useAdditionalInfo.getState();
  const wasInitialized = initialState._initialized;

  while (Date.now() - startTime < maxWaitMs) {
    const state = useAdditionalInfo.getState();

    // If store has valid data, we're good
    if (state.location?.value) {
      return true;
    }

    // If store was initialized but now is reset, stop waiting (token refresh failed)
    if (wasInitialized && !state._initialized) {
      return false;
    }

    // If store just got initialized with data
    if (state._initialized && state.location?.value) {
      return true;
    }

    // Wait a bit before checking again
    await new Promise((resolve) => setTimeout(resolve, checkInterval));
  }

  return false; // Timeout reached
};

// Redirect tracking to prevent infinite loops
const REDIRECT_COUNT_KEY = "auth_redirect_count";
const MAX_REDIRECTS = 3;
const REDIRECT_RESET_DELAY = 5000; // 5 seconds

const canRedirect = (): boolean => {
  const count = Number(sessionStorage.getItem(REDIRECT_COUNT_KEY) || "0");
  return count < MAX_REDIRECTS;
};

const incrementRedirectCount = (): void => {
  const count = Number(sessionStorage.getItem(REDIRECT_COUNT_KEY) || "0");
  sessionStorage.setItem(REDIRECT_COUNT_KEY, String(count + 1));

  // Reset counter after delay
  setTimeout(() => {
    sessionStorage.removeItem(REDIRECT_COUNT_KEY);
  }, REDIRECT_RESET_DELAY);
};

const performSafeRedirect = (path: string): void => {
  const currentPath = window.location.pathname;

  // Don't redirect if already on login page
  if (currentPath === "/login" || currentPath.startsWith("/login")) {
    console.warn("Already on login page, skipping redirect to prevent loop");
    return;
  }

  // Check redirect count
  if (!canRedirect()) {
    console.error("Max redirect attempts reached. Preventing infinite loop.");
    return;
  }

  incrementRedirectCount();
  window.location.href = path;
};

/**
 * Trigger TPass login automatically on 401
 * This function will redirect to TPass login and never return
 */
const triggerTpassLogin = async (): Promise<never> => {
  try {
    const clientId = EnvVariables.clientId;
    const redirectUri = window.location.origin;

    // Save current path and force AdditionalInfo after login for business pages.
    requireAdditionalInfoOnNextLogin(undefined, { force: true });

    // Clean up old session data before redirecting to TPass login
    // This ensures fresh session/token after re-login
    console.log("[401 Auto-Login] Cleaning up old session data...");
    sessionSocketService.clearSession();
    sessionSocketService.disconnect();
    LocalStorage.clearTokens();
    LocalStorage.clearSession();
    LocalStorage.removeItem("session_initializing");
    sessionStorage.removeItem("user");

    // Preserve AdditionalInfo for same-user re-login after session expiry.
    // saveUserSession will reset it later if the authenticated user changes.
    clearPersistedStores();
    resetAllStores({ preserveAdditionalInfo: true });

    // Generate and store code verifier
    const codeVerifier = generateCodeVerifier();
    storage.sessionStore.safeSet(storage.CODE_VERIFIER_KEY, codeVerifier);

    // Generate code challenge
    const codeChallenge = await generateChallenge(codeVerifier);
    const authState = createAuthReturnState();
    const stateParam = authState ? `&state=${encodeURIComponent(authState)}` : "";

    // Construct TPass login URL
    const loginUrl = `${EnvVariables.tpassLoginUrl}?redirect_uri=${encodeURIComponent(redirectUri)}&code_challenge=${encodeURIComponent(codeChallenge)}&client_id=${encodeURIComponent(clientId)}${stateParam}`;

    console.log("[401 Auto-Login] Redirecting to TPass login...");

    // Redirect to TPass login
    window.location.href = loginUrl;

    // Return a promise that never resolves to prevent further code execution
    // This keeps the request in pending state until the page unloads
    return new Promise<never>(() => {});
  } catch (error) {
    console.error("[401 Auto-Login] Failed to trigger TPass login:", error);
    // Fallback to regular login page
    performSafeRedirect("/login");
    // Still return never-resolving promise
    return new Promise<never>(() => {});
  }
};

// Process all pending requests
const processPendingRequests = (token: string | null) => {
  pendingRequests.forEach((request) => {
    if (token) {
      request.resolve(token);
    } else {
      request.reject("Could not refresh token");
    }
  });

  pendingRequests = [];
};

// Request interceptor
export const requestInterceptor: RequestInterceptor = {
  onFulfilled: async (config: InternalAxiosRequestConfig) => {
    const cloneConfig = { ...config };
    let url = cloneConfig.url || "";

    // Apply version rewriting in non-development environments
    // In development: keep URLs relative for the Vite proxy so local dev URLs (VITE_*_LOCAL_URL) can
    // work; visit and billing URLs only gain the v2 segment when the user's choice names v2
    if (!isDevelopment()) {
      const absoluteUrl = rewriteAbsolutePath(url);
      if (absoluteUrl !== url) {
        // console.log(`[Axios] Routing URL: ${url} -> ${absoluteUrl}`);
        url = absoluteUrl;
        cloneConfig.url = absoluteUrl;
      }
    } else {
      url = rewriteRelativePathVersion(url);
      cloneConfig.url = url;
    }

    // Wait for session initialization before proceeding with non-auth requests
    // Skip waiting for: session register endpoint itself, health checks, and auth endpoints
    const isSessionRegisterRequest = url.includes("/session/register");
    const isSessionRefreshRequest = url.includes("/session/refresh");
    const isSessionApiRequest = isSessionRegisterRequest || isSessionRefreshRequest;
    // Exact path match: a bare substring check classified
    // /archive/.../health-screenings as a health check, dropping x-role and
    // getting the request 403'd by role-gated routes (ECHO-3965).
    const isHealthCheck = /\/health(\?|$)/.test(url);
    const isLoginRequest = url.includes("/login");
    const isOrganisationApi = /\/api\/organisations($|\/|\?)/.test(url);
    const isPublicRequest = isHealthCheck || isLoginRequest || isOrganisationApi;
    const requestId = isLoginRequest
      ? undefined
      : getAnalyticsMeta(cloneConfig)?.feRequestId || createFeRequestId();
    cloneConfig.headers = cloneConfig.headers || {};
    if (requestId) {
      cloneConfig.headers[FE_REQUEST_ID_HEADER] = requestId;
    }

    // Track request start for analytics
    const analyticsMeta: AnalyticsMeta = {
      startTime: nowMs(),
      routePath: getCurrentRoutePath(),
    };
    if (requestId) {
      analyticsMeta.feRequestId = requestId;
    }
    (cloneConfig as InternalAxiosRequestConfig & { __analytics?: AnalyticsMeta }).__analytics =
      analyticsMeta;
    addSentryBreadcrumb("api.request", url, {
      ...getApiRequestMeta(cloneConfig),
      ...(requestId ? { [FE_REQUEST_ID_PROPERTY]: requestId } : {}),
    });

    let token = LocalStorage.getAccessToken();

    // Check if token exists and is expired
    if (token && isTokenExpired(token)) {
      // Try to refresh the token
      const newToken = await refreshToken();
      if (newToken) {
        token = newToken;
      } else {
        return await triggerTpassLogin();
      }
    }

    if (token) {
      cloneConfig.headers = cloneConfig.headers || {};
      cloneConfig.headers.Authorization = `Bearer ${token}`;
    } else if (!isPublicRequest) {
      const error = new Error("Access token not available") as Error & { __CANCEL__: boolean };
      error.__CANCEL__ = true;
      return Promise.reject(error);
    }

    if (!isSessionApiRequest && !isHealthCheck && !isLoginRequest) {
      await waitForSessionInitialization();
    }

    const sessionId = LocalStorage.getSessionId();

    // Session register should not send session ID; refresh requires x-session-id
    const shouldSkipSessionId = isSessionRegisterRequest;
    if (sessionId && !shouldSkipSessionId) {
      cloneConfig.headers = cloneConfig.headers || {};
      cloneConfig.headers["x-session-id"] = sessionId;
    }

    const room = getRoom();
    if (room) {
      cloneConfig.headers["x-room-id"] = room.value;
    }

    // Active role, read at request time so a role switch takes effect on the next request.
    // Only permission-gated requests carry it; the auth service would reject the preflight.
    if (shouldSendActiveRole({ isSessionApiRequest, isHealthCheck, isLoginRequest })) {
      applyActiveRoleHeader(cloneConfig.headers, useAdditionalInfo.getState().role);
    }

    // Add clinic-id, user-id and username to request headers
    try {
      // Note: 'user' is a Zustand persist store, which now has environment prefix
      const currentEnv = import.meta.env.VITE_ENV || "development";
      const userStr = localStorage.getItem(`${currentEnv}-user`);
      let clinicId = getClinicId();

      // Requests that should NOT require/wait for clinicId
      const isAuthRequestForClinic = isLoginRequest || isSessionApiRequest;
      const isHealthCheckForClinic = url.includes("/health");
      const isSessionApi = isSessionApiRequest;
      // URL already contains clinicId as path parameter (e.g., /organisations/{orgId}/clinics/{clinicId}/rooms)
      const hasClinicIdInUrl = url.match(/\/organisations\/[^/]+\/clinics\/[^/]+/);

      // Check if this request needs clinicId
      const needsClinicId =
        !isAuthRequestForClinic &&
        !isHealthCheckForClinic &&
        !isSessionApi &&
        !isOrganisationApi &&
        !hasClinicIdInUrl &&
        (url.includes("/clinics/") ||
          url.includes("/api/outpatient") ||
          url.includes("/api/procedure") ||
          url.includes("/api/billing") ||
          url.includes("/api/queue"));

      // If clinicId is missing but needed, wait for store rehydration
      if (!clinicId && needsClinicId && token) {
        // Wait for store to rehydrate from localStorage (max 1 second)
        const rehydrated = await waitForStoreRehydration(1000);
        if (rehydrated) {
          // Try to get clinicId again after rehydration
          clinicId = getClinicId();
        }

        // If still no clinicId after waiting, it means user hasn't selected one
        if (!clinicId) {
          // Cancel the request silently
          const error = new Error("Clinic not selected") as Error & { __CANCEL__: boolean };
          error.__CANCEL__ = true;
          return Promise.reject(error);
        }
      }

      if (userStr) {
        const user = JSON.parse(userStr);
        cloneConfig.headers = cloneConfig.headers || {};
        if (user.id) {
          cloneConfig.headers["x-user-id"] = user.id;
        }
        if (user.username) {
          cloneConfig.headers["x-username"] = user.username;
        }
      }

      if (isOrganisationApi) {
        let orgApiKey = EnvVariables.apiKeyOrganization;
        // Fix common escaping issues
        orgApiKey = orgApiKey.replace(/\\\\/g, "\\"); // Fix double backslash
        cloneConfig.headers["x-api-key"] = orgApiKey;
      }

      if (clinicId) {
        cloneConfig.headers["x-clinic-id"] = clinicId;
      }
    } catch (error) {
      // Check if it's our cancel error
      if (error && typeof error === "object" && "__CANCEL__" in error) {
        return Promise.reject(error);
      }
      // Silently catch other errors during interceptor setup
      // This handles cases where clinicId is missing during logout/token refresh
    }

    cloneConfig.headers = cloneConfig.headers || {};

    return cloneConfig;
  },
  onRejected: (error: AxiosError) => Promise.reject(error),
};

// Response interceptor
export const responseInterceptor: ResponseInterceptor = {
  onFulfilled: async (response: AxiosResponse) => {
    trackApiPerformance(response.config, response.status, true);

    // Skip transformation for file downloads
    if ((response.config as { skipResponseTransform?: boolean })?.skipResponseTransform) {
      return response;
    }

    // Check if the response is HTML instead of JSON (indicates routing issue)
    const contentType = normalizeHeaderValue(response.headers?.["content-type"]).toLowerCase();
    const url = response.config?.url || "Unknown URL";

    if (
      contentType.includes("text/html") ||
      (typeof response.data === "string" && response.data.includes("<!doctype html>"))
    ) {
      const error = new Error(
        `API endpoint not found: ${url}. Received HTML instead of JSON response.`,
      ) as Error & {
        isApiNotFound: boolean;
        originalUrl: string;
        statusCode: number;
      };
      error.isApiNotFound = true;
      error.originalUrl = url;
      error.statusCode = response.status;

      return Promise.reject(error);
    }

    if (
      response.data &&
      typeof response.data === "object" &&
      "status" in response.data &&
      "statusCode" in response.data &&
      "data" in response.data
    ) {
      return response;
    }

    // Transform the response to the required structure
    const originalData = response.data;
    const statusCode = response.status;

    // Create the standardized response structure
    response.data = {
      status: statusCode >= 200 && statusCode < 300 ? "success" : "error",
      statusCode: statusCode,
      data: originalData,
      meta: response.headers?.meta || {},
    };

    return response;
  },
  onRejected: async (error: unknown) => {
    // Handle canceled requests (missing clinicId)
    if (error && typeof error === "object" && "__CANCEL__" in error) {
      // Silently reject without logging
      return Promise.reject(error);
    }
    if (axios.isCancel(error) || (axios.isAxiosError(error) && error.code === "ERR_CANCELED")) {
      return Promise.reject(error);
    }

    if (!axios.isAxiosError(error)) {
      return Promise.reject(error);
    }

    const axiosError = error;

    const recordFailure = () => {
      trackApiPerformance(axiosError.config, axiosError.response?.status, false);
      trackApiError(axiosError);
    };

    // Handle 404 Not Found error - keep the user on the current page and let callers decide
    if (axiosError.response?.status === 404) {
      console.error("API 404 Response:", {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
      });
      axiosError.message =
        extractApiErrorMessage(axiosError) || "Requested resource was not found.";
    }

    // Handle 500 Internal Server Error - keep the user on the current page and let callers decide
    if (axiosError.response?.status === 500) {
      console.error("API 500 Response:", {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
      });
      axiosError.message =
        extractApiErrorMessage(axiosError) || "An unexpected server error occurred.";
    }

    // Handle cases where the response is HTML (API endpoint not found)
    const url = axiosError.config?.url || "Unknown URL";
    const contentType = normalizeHeaderValue(
      axiosError.response?.headers?.["content-type"],
    ).toLowerCase();

    if (
      axiosError.response?.data &&
      (contentType.includes("text/html") ||
        (typeof axiosError.response.data === "string" &&
          axiosError.response.data.includes("<!doctype html>")))
    ) {
      const apiError = new Error(
        `API endpoint not found: ${url}. Server returned HTML instead of JSON.`,
      ) as Error & {
        isApiNotFound: boolean;
        originalUrl: string;
        statusCode: number;
      };
      apiError.isApiNotFound = true;
      apiError.originalUrl = url;
      apiError.statusCode = axiosError.response.status;

      const requestMeta = getApiRequestMeta(
        axiosError.config || ({} as InternalAxiosRequestConfig),
      );
      const analyticsMeta = getAnalyticsMeta(axiosError.config);
      const routePath = analyticsMeta?.routePath || getCurrentRoutePath();
      trackApiPerformance(axiosError.config, axiosError.response?.status, false);
      analytics.trackError("API Error", sanitizeErrorMessage(apiError.message), {
        error_name: normalizeErrorName(apiError),
        error_stack: sanitizeErrorStack(apiError.stack),
        api_path: requestMeta.path,
        api_host: requestMeta.host,
        api_method: requestMeta.method,
        api_status: axiosError.response.status,
        route_path: routePath,
        [FE_REQUEST_ID_PROPERTY]: analyticsMeta?.feRequestId,
      });
      captureGlobalError(apiError, "API HTML Response", {
        api_path: requestMeta.path,
        api_host: requestMeta.host,
        api_method: requestMeta.method,
        api_status: axiosError.response.status,
        route_path: routePath,
        [FE_REQUEST_ID_PROPERTY]: analyticsMeta?.feRequestId,
      });

      return Promise.reject(apiError);
    }

    const errorResponse = axiosError.response?.data;
    if (errorResponse && typeof errorResponse === "object") {
      if ("message" in errorResponse) {
        axiosError.message = errorResponse.message as string;
      }

      if ("data" in errorResponse && axiosError.response) {
        axiosError.response.data = errorResponse.data;
      }

      if ("errors" in errorResponse) {
        if (Array.isArray(errorResponse.errors) && errorResponse.errors.length) {
          axiosError.message = errorResponse.errors?.[0]?.message;
        }
      }
    }

    if (isConflictError(axiosError)) {
      const conflictMessage = extractApiErrorMessage(axiosError);
      if (conflictMessage) {
        axiosError.message = conflictMessage;
      }
    }

    // Handle 401 Unauthorized error (token expired)
    if (axiosError.response?.status === 401) {
      const originalRequest = axiosError.config;

      if (originalRequest) {
        // If we're not already refreshing the token
        if (!isRefreshing) {
          isRefreshing = true;

          try {
            // Try to refresh the token
            const newToken = await refreshToken();

            if (newToken) {
              // Update the request header with new token
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;

              // Process all pending requests
              processPendingRequests(newToken);

              // Retry the original request with the new token
              return await axios.request(originalRequest);
            } else {
              // If refresh failed, process all pending requests with null
              processPendingRequests(null);

              // Reset app state but keep organization/clinic selection for same-user re-login.
              resetAllStores({ preserveAdditionalInfo: true });

              // Automatically trigger TPass login on 401
              // This function returns a never-resolving promise and redirects the page
              return await triggerTpassLogin();
            }
          } catch {
            processPendingRequests(null);

            // Reset app state but keep organization/clinic selection for same-user re-login.
            resetAllStores({ preserveAdditionalInfo: true });

            // Automatically trigger TPass login on 401
            // This function returns a never-resolving promise and redirects the page
            return await triggerTpassLogin();
          } finally {
            isRefreshing = false;
          }
        } else {
          // If we are already refreshing, add this request to pending
          return new Promise((resolve, reject) => {
            pendingRequests.push({
              resolve: (token) => {
                if (token && originalRequest) {
                  originalRequest.headers = originalRequest.headers || {};
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  resolve(axios.request(originalRequest));
                } else {
                  reject(axiosError);
                }
              },
              reject: (reason) => {
                reject(reason);
              },
            });
          });
        }
      }
    }

    // Handle 403 Forbidden error (insufficient permissions)
    if (axiosError.response?.status === 403) {
      recordFailure();
      console.error("403 Forbidden: Insufficient permissions", {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
      });
      // Don't redirect for 403 - just reject the error
      // The UI should handle permission errors appropriately
      return Promise.reject(axiosError);
    }

    // Format error response to match success structure
    if (axiosError.response) {
      axiosError.response.data = {
        status: "error",
        statusCode: axiosError.response.status,
        data: errorResponse || axiosError.message,
        meta: {},
      };
    }

    recordFailure();
    return Promise.reject(axiosError);
  },
};

/**
 * Set axios request and response interceptors
 * @param instance axios instance
 */
export const setupInterceptors = (instance: AxiosInstance): void => {
  instance.interceptors.request.use(requestInterceptor.onFulfilled, requestInterceptor.onRejected);

  instance.interceptors.response.use(
    responseInterceptor.onFulfilled,
    responseInterceptor.onRejected,
  );
};
